import { RoleTypes } from '@/portainer/rbac/models/role';
import { Role, UserId } from '@/portainer/users/types';
import { EnvironmentGroupId } from '@/react/portainer/environments/types';
import { AuthenticationMethod } from '@/react/portainer/settings/types';

/** Placeholder the server replaces with the Portainer user ID. */
export const USER_ID_PLACEHOLDER = '{{user_id}}';

/**
 * Authentication methods the server accepts as ANP user scopes. Mirrors the
 * backend Validate() rules in
 * package/server-ee/api/policies/automatic_namespace_k8s.go.
 */
export const USER_SCOPE_OPTIONS: ReadonlyArray<{
  value: AuthenticationMethod;
  label: string;
}> = [
  { value: AuthenticationMethod.Internal, label: 'Internal' },
  { value: AuthenticationMethod.LDAP, label: 'LDAP' },
  { value: AuthenticationMethod.OAuth, label: 'OAuth' },
];

/**
 * Roles the server accepts for the provisioned namespace. Mirrors
 * anpNamespaceRoleIDs in the backend: roles that let Portainer list every
 * namespace (environment administrator, operator, helpdesk) would expose other
 * users' namespaces, so the server rejects them.
 */
export const NAMESPACE_ROLE_IDS: ReadonlyArray<number> = [
  RoleTypes.STANDARD,
  RoleTypes.NAMESPACE_OPERATOR,
  RoleTypes.READ_ONLY,
];

export function isUserScope(value: number): value is AuthenticationMethod {
  return USER_SCOPE_OPTIONS.some((option) => option.value === value);
}

export interface AutomaticNamespaceProvisioningFormValues {
  name: string;
  environmentGroupIds: Array<EnvironmentGroupId>;
  /** Authentication methods, kept as numbers so yup can type the array (see isUserScope). */
  userScopes: Array<number>;
  namespacePattern: string;
  /** Undefined until a role is selected. */
  namespaceRoleId?: number;
}

/**
 * API payload shape sent as the policy `Data` field. Matches the Go
 * K8sAutomaticNamespaceProvisioningPolicy.Data struct.
 */
export interface AutomaticNamespaceProvisioningPolicyPayload {
  UserScopes: Array<AuthenticationMethod>;
  NamespacePattern: string;
  NamespaceRoleID: number;
}

export const AUTOMATIC_NAMESPACE_PROVISIONING_INITIAL_VALUES: AutomaticNamespaceProvisioningFormValues =
  {
    name: '',
    environmentGroupIds: [],
    userScopes: [],
    namespacePattern: '',
    namespaceRoleId: undefined,
  };

/**
 * While the initial administrator keeps the administrator role it already has
 * unrestricted access, so the server doesn't provision a namespace for it.
 */
const INITIAL_ADMIN_USER_ID = 1;

/**
 * Whether the policy provisions a namespace for the user. Mirrors the server:
 * the user's last authentication method must be one of the policy's scopes.
 */
export function isUserInScope(
  userScopes: Array<number>,
  userId: UserId,
  role: Role,
  lastAuthenticationMethod?: AuthenticationMethod
) {
  const isInitialAdmin =
    userId === INITIAL_ADMIN_USER_ID && role === Role.Admin;

  return (
    !isInitialAdmin &&
    lastAuthenticationMethod !== undefined &&
    userScopes.includes(lastAuthenticationMethod)
  );
}

/** Namespace a user with the given ID would receive from this pattern. */
export function generateNamespace(pattern: string, userId: number | string) {
  return pattern.replaceAll(USER_ID_PLACEHOLDER, String(userId));
}
